import { useCallback, useMemo, useState } from 'react';

import { Grid, Typography, useTheme } from '@mui/material';
import { GridRowProps } from '@mui/x-data-grid';
import { InvoiceStatus } from '@prisma/client';
import dayjs from 'dayjs';

import { navigate } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import DateFilterMenuItem from 'src/components/FilterDropDown/components/DateFilterMenuItem';
import SingleValueFilterMenuItem from 'src/components/FilterDropDown/components/SingleValueFilterMenuItem';
import {
    buildFilters,
    createFilterObject,
    DateRangeDetails,
    SingleValueDetails,
} from 'src/components/FilterDropDown/utils';
import { NPIDataGrid, type NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { DEFAULT_BORDER } from 'src/constants';
import { GET_SALES_INVOICES_PAGINATED } from 'src/graphql/queries/getSalesInvoicesPaginated.query';
import useStablePagination from 'src/hooks/useStablePagination';
import useTable from 'src/hooks/useTable';
import { resolvePageSize } from 'src/lib/pagination';

import { CellParams } from './index.types';

const ViewSalesInvoicesPage = () => {
    const theme = useTheme();

    const {
        rows,
        pagination,
        fetchMore,
        updateSorting,
        updateFilters,
        loading,
        sortState,
        filterState,
        reset,
    } = useTable({
        query: GET_SALES_INVOICES_PAGINATED,
        pagination: {
            page: 1,
            pageSize: resolvePageSize(5),
        },
        sorting: [],
        filterModel: { items: [] },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [status, setStatus] = useState<InvoiceStatus | null>(null);
    const invoiceStatusArray = Object.values(InvoiceStatus);

    const buildColumns = (): NPIDataGridColDef[] => {
        return [
            {
                field: 'invoiceNumber',
                headerName: 'Invoice Number',
                flex: 1,
            },
            {
                field: 'merchant.displayName',
                headerName: 'Merchant Name',
                flex: 1,
                renderCell: (params: CellParams): string => {
                    const merchantName = params.row?.merchant?.displayName ?? '';
                    return merchantName;
                },
            },
            {
                field: 'merchant.merchantAccountNumber',
                headerName: 'Merchant Account Number',
                flex: 1,
                renderCell: (params: CellParams): string => {
                    return params.row?.merchant?.merchantAccountNumber ?? '';
                },
            },
            {
                field: 'createdAt',
                headerName: 'Date Added',
                flex: 1,
                renderCell: (params: CellParams<string | Date | null | undefined>) => {
                    const value = params?.value;
                    const date = value ? dayjs(value) : null;
                    return <span>{date ? date.format('YYYY-MM-DD') : ''}</span>;
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                renderCell: (params: CellParams<string>) => {
                    return <StatusChip status={params.value ?? ''} type="salesInvoice" />;
                },
            },
        ];
    };

    const columns = useMemo(() => {
        return buildColumns();
    }, []);

    const handleAddInvoice = useCallback(() => {
        navigate('/finance/sales-invoices/add');
    }, []);

    const handleViewInvoice = useCallback(({ id }: { id: number }) => {
        navigate(`/finance/sales-invoice/${id}`);
    }, []);

    const handleOnClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setStatus(null);
        reset();
    };

    const handleOnApplyFilters = () => {
        const dateDetails: DateRangeDetails = {
            field: 'createdAt',
            fromDate: fromDate || null,
            toDate: toDate || null,
        };
        const statusDetails: SingleValueDetails = {
            field: 'status',
            value: status,
        };
        const model = createFilterObject(buildFilters(dateDetails, statusDetails));
        updateFilters(model);
    };

    const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);

    return (
        <>
            <MetaTags title="Sales Invoices" description="Sales Invoices Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid
                    item
                    xs={12}
                    sx={{
                        ...DEFAULT_BORDER,
                        height: '116px',
                        backgroundColor: theme.palette.background.default,
                        boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                        padding: '8px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Sales Invoices
                    </Typography>
                    <Typography>Add and View Sales Invoices as necessary</Typography>
                </Grid>
                <Grid item xs={12}>
                    <NPIDataGrid
                        key="sales-invoices-grid"
                        dataTestId="sales-invoices-data-grid"
                        rows={rows}
                        columns={columns}
                        getRowId={(row: GridRowProps) => {
                            return row.id;
                        }}
                        handleView={handleViewInvoice}
                        serverSideFiltering={{
                            onFilterChange: updateFilters,
                            filterModel: filterState,
                        }}
                        serverSideSorting={{
                            onSortChange: updateSorting,
                            sortModel: sortState,
                        }}
                        pagination={{
                            isEnabled: true,
                            mode: 'server',
                            page: stablePage - 1,
                            pageSize: resolvePageSize(pagination.pageSize),
                            totalRows: stableTotalRows,
                            onPageChange: (page: number, pageSize: number) => {
                                fetchMore({ page: page + 1, pageSize });
                            },
                        }}
                        addFilterButtonConfig={{
                            isFilterActive: fromDate !== null || toDate !== null || status !== null,
                            handleOnClearFilters,
                            handleOnApplyFilters,
                            filters: [
                                <DateFilterMenuItem
                                    key="sales-invoice-date-filter"
                                    title="Date Added"
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    handleFromDateChange={setFromDate}
                                    handleToDateChange={setToDate}
                                    onClearFilter={() => {
                                        setFromDate(null);
                                        setToDate(null);
                                    }}
                                />,
                                <SingleValueFilterMenuItem
                                    key="sales-invoice-status-filter"
                                    title="Status"
                                    options={invoiceStatusArray}
                                    value={status || ''}
                                    onChange={setStatus}
                                    onClearFilter={() => {
                                        setStatus(null);
                                    }}
                                />,
                            ],
                        }}
                        addRecordButtonConfig={{
                            addRecordButtonText: 'Add Sales Invoice',
                            addRecordCallBack: handleAddInvoice,
                        }}
                        handleReset={reset}
                        filtering={{ isEnabled: true }}
                        loading={loading}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default ViewSalesInvoicesPage;
